import { PrivateRoutes } from '@/constants/routes'
import { IConfigSeller } from '@/models/configSeller'
import { webApiService } from '@/services'
import { UTCDateToCountryDate } from '@/utils/countryDate.util'
import { Button, Chip, Input, Pagination, Select, SelectItem, SortDescriptor, Spinner, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@nextui-org/react'
import { motion } from 'framer-motion'
import { enqueueSnackbar } from 'notistack'
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { MdArrowBackIos, MdCloudDownload, MdOutlineDevicesOther, MdOutlineSearch } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import XLSX from 'xlsx-js-style'
import { COLUMNS, DEFAULT_PAGE_SIZE, EXCEL_NAME, MAX_PAGE_SIZE, PAGE_SIZES } from './constants'
import { IDeviceRequest, IDeviceRequestPage } from './models'

const ALL_SBU_KEY = 'all'

const DeviceRequestsPage = () => {
  const navigate = useNavigate()
  const { slug } = useParams<{ slug: string }>()
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const idSeller = configSeller.config.idSeller

  const [data, setData] = useState<{ loading: boolean, loadingExcel: boolean, list: IDeviceRequest[], count: number, totalPages: number }>({
    loading: false,
    loadingExcel: false,
    list: [],
    count: 0,
    totalPages: 0
  })
  const [sbuList, setSbuList] = useState<any[]>([])
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [idSbu, setIdSbu] = useState(ALL_SBU_KEY)
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({ column: 'id', direction: 'descending' })
  // El input se escribe libre y recién a los 500 ms se dispara la búsqueda en servidor.
  const [filterValue, setFilterValue] = useState('')
  const [search, setSearch] = useState('')

  // Descarta respuestas de peticiones viejas que lleguen después de una más reciente.
  const requestId = useRef(0)

  const filters = useMemo(() => ({
    seller: String(slug),
    id_seller: idSeller,
    id_sbu: idSbu === ALL_SBU_KEY ? undefined : Number(idSbu),
    search: search === '' ? undefined : search
  }), [slug, idSeller, idSbu, search])

  const pages = data.totalPages

  // El backend pagina y ordena por id desc; acá solo se reordena la página cargada.
  const sortedItems = useMemo(() => {
    return [...data.list].sort((a: any, b: any) => {
      const first = a[sortDescriptor.column as keyof IDeviceRequest]
      const second = b[sortDescriptor.column as keyof IDeviceRequest]
      if (first === null || first === undefined) return 1
      if (second === null || second === undefined) return -1
      const cmp = first < second ? -1 : first > second ? 1 : 0
      return sortDescriptor.direction === 'descending' ? -cmp : cmp
    })
  }, [sortDescriptor, data.list])

  const loadSbu = async () => {
    try {
      const res = await webApiService.getAllSbu(String(slug))
      const visibles = configSeller.config.permisos.SBUS_VISIBLES ?? []
      setSbuList(res.success ? res.data.filter((item: any) => visibles.includes(item.id_sbu)) : [])
    } catch (error: any) {
      console.log(error)
      setSbuList([])
    }
  }

  const loadData = async () => {
    const currentRequest = ++requestId.current
    setData(prev => ({ ...prev, loading: true }))
    try {
      const res = await webApiService.getDeviceRequests({ ...filters, page, pageSize: rowsPerPage })
      if (currentRequest !== requestId.current) return
      const result: IDeviceRequestPage | null = res.success ? res.data : null
      setData(prev => ({
        ...prev,
        loading: false,
        list: result?.items ?? [],
        count: result?.total ?? 0,
        totalPages: result?.totalPages ?? 0
      }))
    } catch (error: any) {
      if (currentRequest !== requestId.current) return
      console.log(error)
      enqueueSnackbar('No pudimos cargar los dispositivos solicitados.', { variant: 'error' })
      setData(prev => ({ ...prev, loading: false, list: [], count: 0, totalPages: 0 }))
    }
  }

  useEffect(() => {
    loadSbu()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setSearch(filterValue.trim()), 500)
    return () => clearTimeout(timer)
  }, [filterValue])

  // Cualquier cambio de filtro vuelve a la primera página.
  useEffect(() => {
    setPage(1)
  }, [filters, rowsPerPage])

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, rowsPerPage])

  const onSearchChange = (value: string) => setFilterValue(value)

  const onClear = () => setFilterValue('')

  const onNextPage = () => {
    if (page < pages) setPage(page + 1)
  }

  const onPreviousPage = () => {
    if (page > 1) setPage(page - 1)
  }

  const onRowsPerPageChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value))
  }

  // La descarga trae todas las solicitudes que cumplen los filtros activos: el
  // backend entrega como máximo 100 por llamada, así que se recorren todas las páginas.
  const exportToCSV = async () => {
    setData(prev => ({ ...prev, loadingExcel: true }))
    try {
      const rows: IDeviceRequest[] = []
      let currentPage = 1
      let totalPages = 1
      while (currentPage <= totalPages) {
        const res = await webApiService.getDeviceRequests({ ...filters, page: currentPage, pageSize: MAX_PAGE_SIZE })
        const result: IDeviceRequestPage | null = res.success ? res.data : null
        if (result === null) break
        rows.push(...result.items)
        totalPages = result.totalPages
        currentPage++
      }

      if (rows.length === 0) {
        enqueueSnackbar('No hay solicitudes para descargar con los filtros aplicados.', { variant: 'info' })
        return
      }

      const sheetRows = rows.map((item) => {
        const row: any = {}
        COLUMNS.forEach((column) => {
          row[column.name] = column.uid === 'created_at'
            ? UTCDateToCountryDate(item.created_at)
            : (item as any)[column.uid]
        })
        return row
      })

      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(sheetRows)
      XLSX.utils.book_append_sheet(workbook, worksheet, EXCEL_NAME)
      XLSX.writeFile(workbook, `${EXCEL_NAME}.xlsx`)
    } catch (error: any) {
      console.log(error)
      enqueueSnackbar('No pudimos generar la descarga.', { variant: 'error' })
    } finally {
      setData(prev => ({ ...prev, loadingExcel: false }))
    }
  }

  const renderCell: any = (item: IDeviceRequest, columnKey: any) => {
    const cellValue = (item as any)[columnKey]

    /* eslint default-case: "error" */
    switch (columnKey) {
      case 'created_at':
        return (<span className='text-xs'>{UTCDateToCountryDate(cellValue)}</span>)
      case 'sbu':
        return cellValue !== null && cellValue !== ''
          ? (<Chip color='primary' size='sm' variant='flat'>{cellValue}</Chip>)
          : (<span className='text-xs'>-</span>)
      case 'brand':
        // id_brand nulo = marca escrita a mano por el cliente ("Otra").
        return (
          <div className='flex items-center gap-1'>
            <span className='font-bold text-xs'>{cellValue ?? '-'}</span>
            {item.id_brand === null && cellValue !== null ? <Chip color='warning' size='sm' variant='flat'>Nueva</Chip> : null}
          </div>
        )
      default:
        return (<span className='text-xs'>{cellValue ?? '-'}</span>)
    }
  }

  const bottomContent = (
    <div className='py-2 px-2 flex justify-between items-center'>
      {
        data.loading
          ? <div>loading ...</div>
          : <Pagination
              isCompact
              showControls
              showShadow
              color='primary'
              page={page}
              total={pages || 1}
              onChange={setPage}
            />
      }
      <div className='hidden sm:flex w-[30%] justify-end gap-2'>
        <Button isDisabled={page <= 1} size='sm' variant='flat' onPress={onPreviousPage}>
          Previous
        </Button>
        <Button isDisabled={page >= pages} size='sm' variant='flat' onPress={onNextPage}>
          Next
        </Button>
      </div>
      <div className='flex justify-between items-center'>
        <span className='text-default-400 text-small mr-3'>Total: {data.count} registros</span>
        <label className='flex items-center text-default-400 text-small'>
          Filas por página:
          <select
            className='bg-transparent outline-none text-default-400 text-small'
            onChange={onRowsPerPageChange}
            value={rowsPerPage}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </label>
      </div>
    </div>
  )

  return (
    <motion.div
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -10, opacity: 0 }}
      transition={{ duration: 0.3 }} className='space-y-4'
    >
      <div className='flex items-center'>
        <Button
          variant='ghost' size='sm' color='primary' onPress={() => navigate(PrivateRoutes.HOME.replace(':slug', String(slug)))} startContent={
            <MdArrowBackIos className='text-black/50 mb-0.5 dark:text-white/90 text-slate-400 pointer-events-none flex-shrink-0' />
          }
        >Volver
        </Button>
        <h1 className='font-black md:text-2xl ml-2'>Dispositivos solicitados</h1>
      </div>

      <div className='flex items-start gap-2 rounded-xl border-1 border-gray-200 bg-white/70 p-3 text-sm text-gray-600'>
        <MdOutlineDevicesOther className='shrink-0 text-xl' />
        <span>Dispositivos que no se encontraron en el cotizador y que fueron solicitados para incorporarlos al catálogo de Trade-in.</span>
      </div>

      <div className='bg-white rounded-2xl shadow-md p-2 mt-4'>
        <div className='grid grid-cols-2'>
          <div className='my-4 flex items-center gap-3'>
            <Input
              label='Search'
              isClearable
              radius='lg'
              value={filterValue}
              onValueChange={onSearchChange}
              onClear={() => onClear()}
              classNames={{
                label: 'text-black/50 dark:text-white/90',
                input: [
                  'bg-transparent',
                  'text-black/90 dark:text-white/90',
                  'placeholder:text-default-700/50 dark:placeholder:text-white/60'
                ],
                innerWrapper: 'bg-transparent',
                inputWrapper: [
                  'bg-default-200/50',
                  'dark:bg-default/60',
                  'backdrop-blur-xl',
                  'backdrop-saturate-200',
                  'hover:bg-default-200/70',
                  'dark:hover:bg-default/70',
                  'group-data-[focus=true]:bg-default-200/50',
                  'dark:group-data-[focus=true]:bg-default/60',
                  '!cursor-text'
                ]
              }}
              placeholder='Buscar...'
              startContent={
                <MdOutlineSearch className='text-black/50 mb-0.5 dark:text-white/90 text-slate-400 pointer-events-none flex-shrink-0' />
          }
            />
            <Select
              label='Categoría'
              placeholder='Categoría'
              selectedKeys={[idSbu]}
              onChange={(e) => {
                if (e.target.value === '') return
                setIdSbu(e.target.value)
              }}
              className='w-[300px]'
              variant='bordered'
              classNames={{ trigger: 'bg-white' }}
            >
              {[
                <SelectItem key={ALL_SBU_KEY} value={ALL_SBU_KEY} textValue='Todas'>Todas</SelectItem>,
                ...sbuList.map((item: any) => (
                  <SelectItem key={String(item.id_sbu)} value={String(item.id_sbu)} textValue={item.device}>
                    {item.device}
                  </SelectItem>
                ))
              ]}
            </Select>
          </div>
          <div className='flex items-center justify-end'>
            <Button color='success' isLoading={data.loadingExcel} className='text-white' variant='shadow' onPress={exportToCSV} endContent={<MdCloudDownload />}>Descargar</Button>
          </div>
        </div>
        <Table
          aria-label='Listado de dispositivos solicitados'
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
          bottomContent={bottomContent}
          bottomContentPlacement='outside'
        >
          <TableHeader columns={COLUMNS}>
            {(column) => (
              <TableColumn key={column.uid} align='start' allowsSorting className='md:table-cell'>
                {column.name}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody
            isLoading={data.loading}
            loadingContent={<Spinner label='Loading...' />}
            emptyContent={data.loading ? ' ' : 'No hay dispositivos solicitados.'}
          >
            {
              sortedItems.map((item: IDeviceRequest) => (
                <TableRow key={item.id}>
                  {(columnKey) => <TableCell>{renderCell(item, columnKey)}</TableCell>}
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </div>
    </motion.div>
  )
}

export default DeviceRequestsPage
